import apiClient, { handleApiResponse } from './client';
import type {
  LoginCredentials,
  RegisterData,
  AuthTokens,
  User,
  ApiResponse,
} from '@types';

export const authApi = {
  // Login user
  login: async (credentials: LoginCredentials): Promise<AuthTokens> => {
    const response = await apiClient.post<ApiResponse<AuthTokens>>(
      '/v1/auth/login',
      credentials
    );
    return handleApiResponse(response);
  },

  // Register new user
  register: async (data: RegisterData): Promise<AuthTokens> => {
    const response = await apiClient.post<ApiResponse<AuthTokens>>(
      '/v1/auth/register',
      data
    );
    return handleApiResponse(response);
  },

  // Get current user
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<ApiResponse<User>>('/v1/auth/me');
    return handleApiResponse(response);
  },

  // Refresh access token
  refreshToken: async (refreshToken: string): Promise<AuthTokens> => {
    const response = await apiClient.post<ApiResponse<AuthTokens>>(
      '/v1/auth/refresh',
      { refresh_token: refreshToken }
    );
    return handleApiResponse(response);
  },

  // Logout user
  logout: async (): Promise<void> => {
    await apiClient.post('/v1/auth/logout');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  },

  // Request password reset
  requestPasswordReset: async (email: string): Promise<void> => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/v1/auth/password-reset/request',
      { email }
    );
    return handleApiResponse(response);
  },

  // Reset password
  resetPassword: async (token: string, newPassword: string): Promise<void> => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/v1/auth/password-reset/confirm',
      { token, new_password: newPassword }
    );
    return handleApiResponse(response);
  },

  // Update user profile
  updateProfile: async (data: {
    first_name?: string;
    last_name?: string;
    company_name?: string;
  }): Promise<User> => {
    const response = await apiClient.put<ApiResponse<User>>(
      '/v1/auth/profile',
      data
    );
    return handleApiResponse(response);
  },

  // Change password
  changePassword: async (
    currentPassword: string,
    newPassword: string
  ): Promise<void> => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/v1/auth/change-password',
      { current_password: currentPassword, new_password: newPassword }
    );
    return handleApiResponse(response);
  },
};

export default authApi;
